import clsx from "clsx";

import type { PillProps } from "@proda-ai/ui-kit";
import { Pill } from "@proda-ai/ui-kit";

import Box from "@/ui/atoms/Box";

export type BadgeProps = JSX.IntrinsicAttributes &
    Partial<Omit<PillProps, "leadingIcon">> & {
        severity?: "success" | "warning" | "error" | "info" | "inactive";
        customColor?: {
            backgroundColor: PillProps["backgroundColor"];
            leadingIconColor: PillProps["backgroundColor"];
            borderColor: PillProps["borderColor"];
        };
        children?: React.ReactNode;
        leadingIcon?: PillProps["leadingIcon"] | null;
    };

const testId = "Badge";
const Badge = (props: BadgeProps) => {
    const {
        children,
        className,
        severity,
        customColor,
        leadingIcon: leadingIconProp,
        ...otherProps
    } = props;

    let backgroundColor: PillProps["backgroundColor"];
    let leadingIconColor: PillProps["backgroundColor"];
    let borderColor: PillProps["borderColor"];
    const textColor = "proda-black";

    if (customColor) {
        backgroundColor = customColor.backgroundColor;
        leadingIconColor = customColor.leadingIconColor;
        borderColor = customColor.borderColor;
    } else {
        switch (severity) {
            case "success":
                backgroundColor = "proda-green-20";
                leadingIconColor = "proda-green-120";
                borderColor = "proda-green-120";
                break;
            case "warning":
                backgroundColor = "proda-yellow-20";
                leadingIconColor = "proda-yellow-120";
                borderColor = "proda-yellow-120";
                break;
            case "error":
                backgroundColor = "proda-red-20";
                leadingIconColor = "proda-red-120";
                borderColor = "proda-red-120";
                break;
            case "inactive":
                // @ts-expect-error we do have the color in the theme but its not present in UI-Kit
                backgroundColor = "proda-white-120";
                leadingIconColor = "proda-black";
                borderColor = "proda-black";
                break;
            case "info":
                backgroundColor = "proda-light-blue";
                leadingIconColor = "proda-neon-blue";
                borderColor = "proda-neon-blue";
                break;
            default:
                backgroundColor = props.backgroundColor || "proda-light-blue";
                leadingIconColor =
                    props.leadingIcon?.iconColor || "proda-light-blue";
                borderColor = props.borderColor || "proda-light-blue";
                break;
        }
    }

    const displayNoIconProps = {
        leadingIcon: undefined,
    };
    const displayDefaultIconProps = {
        leadingIcon: { icon: "circle" as const, iconColor: leadingIconColor },
    };
    const displayCustomIconProps = {
        leadingIcon: {
            ...displayDefaultIconProps.leadingIcon,
            ...leadingIconProp,
        },
    };
    const displayIconProps = leadingIconProp
        ? displayCustomIconProps
        : leadingIconProp === null
          ? displayNoIconProps
          : displayDefaultIconProps;

    return (
        <Box
            sx={{
                [`& .${testId}`]: {
                    backgroundColor: `var(--${backgroundColor})`,
                    width: "fit-content",
                },
            }}
        >
            <Pill
                data-testid={testId}
                className={clsx([testId, className])}
                borderColor={borderColor}
                textColor={textColor}
                {...displayIconProps}
                {...otherProps}
            >
                {children}
            </Pill>
        </Box>
    );
};

export default Badge;
